'use client';

import { Helmet } from '@/lib/helmet-shim';
import { usePathname } from 'next/navigation';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  structuredData?: object | object[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
  };
}

const BASE_URL = 'https://www.scriptunlock.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;
const SITE_NAME = 'Script Unlock';

const DEFAULT_KEYWORDS = 'prescription savings, pharmacy comparison, medication prices, compounding pharmacy, prescription bids, drug prices, pharmacy finder, prescription discounts, cheap prescriptions, medication savings, pharmacy deals, Rx savings';

export const SEOHead = ({
  title,
  description = 'Upload your prescription and licensed, verified pharmacies each respond with their own cash price. You pay the pharmacy directly. Free for patients.',
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_IMAGE,
  type = 'website',
  noIndex = false,
  structuredData,
  article,
}: SEOHeadProps) => {
  const pathname = usePathname();
  const canonicalUrl = `${BASE_URL}${pathname}`;
  
  const fullTitle = title 
    ? `${title} | ${SITE_NAME}` 
    : `${SITE_NAME}: pharmacies quote a cash price on your prescription`;

  const structuredDataArray = Array.isArray(structuredData) 
    ? structuredData 
    : structuredData 
      ? [structuredData] 
      : [];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Favicon - ensure it's picked up by search engines */}
      <link rel="icon" type="image/png" href="/logo-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon.png" />
      <link rel="shortcut icon" href="/favicon.ico" />
      
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || 'Script Unlock - Save on Prescriptions'} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@script_unlock" />
      <meta name="twitter:creator" content="@script_unlock" />

      {/* Article specific meta (for blog posts, etc) */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {article?.section && (
        <meta property="article:section" content={article.section} />
      )}

      {/* Structured Data */}
      {structuredDataArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
